import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { formatDate } from '../utils/timeFormat';

const COLUMNS = [
  { key: 'artist_name', label: 'Artist', align: 'left', type: 'string' },
  { key: 'track_title', label: 'Track', align: 'left', type: 'string' },
  { key: 'set_title', label: 'Set', align: 'left', type: 'string' },
  {
    key: 'prerelease_track_count',
    label: 'Pre-release tracks',
    align: 'right',
    type: 'number',
  },
  {
    key: 'days_after_release',
    label: 'Days after release',
    align: 'right',
    type: 'number',
  },
  { key: 'status', label: 'Status', align: 'left', type: 'string' },
  {
    key: 'last_outreach_dt',
    label: 'Last outreach',
    align: 'left',
    type: 'date',
  },
];

function compareValues(a, b, type) {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (type === 'number') return Number(a) - Number(b);
  if (type === 'date') return new Date(a).getTime() - new Date(b).getTime();
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
  });
}

const CandidatesTable = ({ items, onRowClick }) => {
  const navigate = useNavigate();
  const [activeCol, setActiveCol] = useState('');
  const [directionAsc, setDirectionAsc] = useState(true);

  const handleTableHeadClick = (column) => {
    if (column.key === activeCol) {
      setDirectionAsc(!directionAsc);
    } else {
      setActiveCol(column.key);
      setDirectionAsc(true);
    }
  };

  const activeColumn = COLUMNS.find((column) => column.key === activeCol);
  const sortedItems = activeColumn
    ? [...items].sort((a, b) => {
        const result = compareValues(
          a[activeColumn.key],
          b[activeColumn.key],
          activeColumn.type
        );
        return directionAsc ? result : -result;
      })
    : items;

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    } else {
      navigate(`/admin/demo/candidates/${row.id}`);
    }
  };

  if (!items.length) return <></>;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ overflow: 'visible' }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="demo candidates table">
        <TableHead>
          <TableRow
            sx={{
              '.MuiTableCell-root': { padding: '16px 8px' },
            }}
          >
            {COLUMNS.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{
                  fontWeight: activeCol === column.key ? 'bold' : 'normal',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => handleTableHeadClick(column)}
              >
                {column.label}
                {activeCol === column.key &&
                  (directionAsc ? (
                    <ArrowUpward
                      fontSize="small"
                      sx={{ verticalAlign: 'top' }}
                    />
                  ) : (
                    <ArrowDownward
                      fontSize="small"
                      sx={{ verticalAlign: 'top' }}
                    />
                  ))}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{
                cursor: 'pointer',
                '.MuiTableCell-root': { padding: 1 },
                ':hover': { backgroundColor: 'info.main' },
              }}
              onClick={() => handleRowClick(row)}
            >
              <TableCell align="left">
                {row.artist_name}
                {row.is_label_boss ? ' (label boss)' : ''}
              </TableCell>
              <TableCell align="left">{row.track_title || '-'}</TableCell>
              <TableCell align="left">{row.set_title || '-'}</TableCell>
              <TableCell align="right">
                {row.prerelease_track_count ?? '-'}
              </TableCell>
              <TableCell align="right">
                {row.days_after_release ?? '-'}
              </TableCell>
              <TableCell align="left">{row.status}</TableCell>
              <TableCell align="left">
                {formatDate(row.last_outreach_dt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

CandidatesTable.propTypes = {
  items: PropTypes.array,
  onRowClick: PropTypes.func,
};

export default CandidatesTable;
